import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useOrganizationSettings from '../../hooks/useOrganizationSettings';
import notify from '../../utils/notify';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading, sessionChecked } = useAuth();
  const { t } = useLanguage();
  const { data: orgSettings } = useOrganizationSettings({ enabled: true });

  const redirectTarget =
    location.state?.from?.pathname && location.state.from.pathname !== '/login'
      ? `${location.state.from.pathname}${location.state.from.search || ''}${location.state.from.hash || ''}`
      : '/dashboard';

  useEffect(() => {
    if (!authLoading && sessionChecked && isAuthenticated) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isAuthenticated, authLoading, sessionChecked, navigate, redirectTarget]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await login({ email, password });
      if (res && res.success) {
        navigate(redirectTarget, { replace: true });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t('login.errorGeneric');
      setError(msg);
      notify.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-slate-800 p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl p-8 shadow-xl border border-slate-200 dark:border-slate-600"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          {orgSettings?.logo ? (
            <img
              src={orgSettings.logo}
              alt=""
              className="mb-3 h-16 w-16 rounded-xl border border-slate-200 object-contain bg-white p-1 dark:border-slate-600"
            />
          ) : null}
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
            {orgSettings?.gymName || t('login.title')}
          </h1>
          {orgSettings?.gymName ? (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{t('login.title')}</p>
          ) : null}
        </div>

        {error ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        ) : null}

        <label className="block mb-4 text-slate-900 dark:text-slate-50">
          <span className="block mb-2 text-sm font-medium">{t('login.emailLabel')}</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/80 text-gray-900 focus:outline-none"
          />
        </label>

        <label className="block mb-6 text-slate-900 dark:text-slate-50">
          <span className="block mb-2 text-sm font-medium">{t('login.passwordLabel')}</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/80 text-gray-900 focus:outline-none"
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-slate-900 dark:text-slate-50 font-semibold transition-colors disabled:opacity-60"
        >
          {loading ? t('login.loading') : t('login.submit')}
        </button>
      </form>
    </div>
  );
};

export default LoginPage;
